import { Button, Card, Form, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { useAppForm } from '../../../lib/app-form';
import { useCreateCompany, useUpdateCompany } from '../hooks/use-companies';
import { useFormSubmitHandlers } from '../hooks/use-form-submit-handlers';
import { type CompanyFormValues, companyFormSchema } from '../schemas';
import { formValuesToRequest } from '../transformers';
import type { CustomerMinimal } from '../types/company';
import { ContextFieldsCard } from './context-fields-card';
import { CustomerSearchSelect } from './customer-search-select';
import { IdentifierFieldsCard } from './identifier-fields-card';

const COMPANIES_LIST_PATH = '/customers/companies';

// How this form lays out the shared field groups: both groups' fields live flat
// at the top level under identical names (companyFormSchema extends the section
// context). The mapping is the parent form's concern, not the cards'.
const SECTION_DETAILS_FIELDS = {
  name: 'name',
  external_id: 'external_id',
  integration_id: 'integration_id',
  accounting_id: 'accounting_id',
} as const;
const SECTION_CONTEXT_FIELDS = {
  customer_group: 'customer_group',
  price_lists: 'price_lists',
  sales_agent_groups: 'sales_agent_groups',
  payment_on_account_enabled: 'payment_on_account_enabled',
  payment_on_account: 'payment_on_account',
} as const;

export function CompanyForm({
  defaultValues,
  isCreate,
  companyId,
  seedPrimaryCustomer,
  seedAdmins,
}: {
  defaultValues: CompanyFormValues;
  isCreate: boolean;
  companyId?: string;
  seedPrimaryCustomer?: CustomerMinimal | null;
  seedAdmins?: CustomerMinimal[];
}) {
  const { navigationHandler } = useHandlers();
  const submitHandlers = useFormSubmitHandlers(COMPANIES_LIST_PATH);

  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany(companyId ?? '');

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: companyFormSchema },
    onSubmit: async ({ value }) => {
      const payload = formValuesToRequest(companyFormSchema.parse(value));

      if (isCreate) {
        await createCompany.mutateAsync(payload, submitHandlers);
      } else if (companyId) {
        await updateCompany.mutateAsync(payload, submitHandlers);
      }
    },
  });

  return (
    <Form onSubmit={form.handleSubmit}>
      <IdentifierFieldsCard
        form={form}
        fields={SECTION_DETAILS_FIELDS}
        translationPrefix="features:companies.pages.company_form_page.details"
      />
      <ContextFieldsCard
        form={form}
        fields={SECTION_CONTEXT_FIELDS}
        contextPrefix="features:companies.pages.company_form_page.context"
      />

      <Card>
        <Card.Header headingLevel="h2">
          {t('features:companies.pages.company_form_page.relations.title')}
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Text variant="secondary">
              {t('features:companies.pages.company_form_page.relations.description')}
            </Text>
            <form.Field name="primary_customer_id">
              {(field) => (
                <CustomerSearchSelect
                  label={t(
                    'features:companies.pages.company_form_page.relations.primary_customer_label',
                  )}
                  required
                  value={field.state.value}
                  onChange={field.handleChange}
                  seed={seedPrimaryCustomer}
                  error={field.state.meta.errors[0]?.message}
                />
              )}
            </form.Field>
            <form.Field name="company_admin_ids">
              {(field) => (
                <CustomerSearchSelect
                  multiple
                  label={t('features:companies.pages.company_form_page.relations.admins_label')}
                  value={field.state.value}
                  onChange={field.handleChange}
                  seed={seedAdmins}
                />
              )}
            </form.Field>
          </div>
        </Card.Body>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          type="button"
          onClick={() => navigationHandler.navigate(COMPANIES_LIST_PATH)}
        >
          {t('buttons:cancel')}
        </Button>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              type="submit"
              disabled={createCompany.isPending || updateCompany.isPending || isSubmitting}
            >
              {t('buttons:save')}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </Form>
  );
}
